import { NextPageContext } from 'next';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { createApolloClient } from '@/config/apollo-client';
import { getHostFromReq, isProduction } from '@/utils/helpers';
import { CONFIG } from '@/constants/config';
import { checkRoute } from '@/components/CustomLink/CustomLink.helpers';
import { Languages, QUERY } from '@/constants/common';
import { DeviceType, getDeviceType } from '@/utils/getDeviceType';
import { initI18nextServer } from '@/i18n-server';

function checkIsReadyRoutes(context: NextPageContext, isDesktop: boolean) {
  // There may be some POST requests coming from the Ruby application,
  // and React should respond with a 404 when the request URL matches the URL of an existing page.
  // (For example, submitting an advertisement)
  if (context.req?.method === 'POST') return false;

  const asPath = context.asPath ?? '';

  const route = asPath.split('?')[0];
  const { query } = context;
  const { isInternal } = checkRoute(route, { query, isDesktop });
  return isInternal;
}

function getLanguage(host: string): Languages {
  const currentLang = host.split('.')[0];
  return CONFIG.languages.includes(currentLang)
    ? (currentLang as Languages)
    : Languages.Az;
}

type InitialPropsData = Record<string, unknown>;

export function withPreRenderSettings(
  gip?: (
    context: NextPageContext,
    client: ApolloClient<NormalizedCacheObject>,
    deviceType: DeviceType
  ) => Promise<InitialPropsData> | InitialPropsData,
  isErrorPage?: boolean
) {
  return async (context: NextPageContext) => {
    const host = context.req
      ? getHostFromReq(context.req)
      : window.location.host;
    const userAgent =
      (context.req
        ? context.req?.headers['user-agent']
        : window.navigator.userAgent) || '';
    const isSSRWebView = userAgent.indexOf('miniApp') >= 0;
    const { deviceType, device } = getDeviceType(userAgent);
    const currentLang = getLanguage(host);
    const i18nServerInstance = await initI18nextServer(currentLang);

    const accessToken = context.query?.[QUERY.access_token];

    const props = {
      currentLang,
      i18nServerInstance,
      isSSRWebView,
      accessToken,
      device,
      deviceType,
    };

    if (!host) return props;

    const isRouteReady = checkIsReadyRoutes(
      context,
      deviceType === DeviceType.Desktop
    );

    if (!isRouteReady && isProduction() && !isErrorPage && context.res) {
      context.res.statusCode = 404;
      context.res.end();
      return;
    }

    if (!gip) return props;

    const apolloClient = createApolloClient({ lang: currentLang, context });

    const initialPropsData = await gip(context, apolloClient, deviceType);

    const apolloState = apolloClient.extract();

    return {
      ...initialPropsData,
      ...props,
      apolloState,
    };
  };
}
